import { DashboardAndAnalysisFiltersPreview } from '@modules/analysis/components/DashboardAndAnalysisFiltersPreview';
import { AndOperatorLabel } from '@modules/analysis/components/DashboardAndAnalysisFiltersPreview/AndOperatorLabel';
import { WhereClauseField } from '@modules/common/components/WhereClauseField';
import { WhereClausePreviewProps } from '@modules/common/components/WhereClausePreview/WhereClausePreview';
import { isEmptyWhereClause } from '@modules/common/utils';
import {
  CombinedDashboardFilterField,
  CombinedQuantifyFilterField,
} from '@modules/quantify/components/QuantifyQueryBuilder/Fields/CombinedDashboadQuantifyFilterFields';
import {
  useFetchQuantifyReport,
  useGetQuantifyDenominatorWhereClause,
  useTriggerQuantifyQueryAnalyticsEvent,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { isValidQuery } from '@modules/quantify/utils';
import { QueryBuilderStoreProvider } from '@modules/queryBuilder/store/QueryBuilderStoreProvider';
import { Box, Stack, Typography } from '@mui/material';
import { AnalysisUseCase } from 'generated/graphql';
import { useCallback, useEffect, useState } from 'react';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import { ExpressionType } from 'v2/core/repository/eql/types';
import {
  EmptyWhereClause,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

interface MeasuredAsMetricDenominatorPreviewProps {
  isReadOnly?: boolean;
  whereClausePreviewProps?: Partial<WhereClausePreviewProps>;
  shouldOpenQBModal: boolean;
  setShouldOpenQBModal: (shouldOpen: boolean) => void;
  shouldIncludeDashboardFilters: boolean;
  shouldShowDashboardFiltersSeparately: boolean;
}

export function MeasuredAsMetricDenominatorPreview({
  isReadOnly,
  whereClausePreviewProps,
  shouldOpenQBModal,
  setShouldOpenQBModal,
  shouldIncludeDashboardFilters,
  shouldShowDashboardFiltersSeparately,
}: MeasuredAsMetricDenominatorPreviewProps) {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const primaryDenominatorWhereClause = useGetQuantifyDenominatorWhereClause({
    shouldIncludeDashboardFilters: false,
  });
  const denominatorWhereClause = useGetQuantifyDenominatorWhereClause();
  const dashboardWhereClause = useQuantifyStore(
    ({ visual }) => visual.dashboardFilters?.whereClause,
  );
  const isValidQueryState = useQuantifyStore(({ query }) =>
    isValidQuery(query),
  );

  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);
  const trackQuantifyQueryEvents = useTriggerQuantifyQueryAnalyticsEvent();
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  useEffect(() => {
    if (shouldFetchReportData && isValidQueryState) {
      setShouldFetchReportData(false);
      fetchReportData();
      trackQuantifyQueryEvents('Quantify - Query Executed');
    }
  }, [
    fetchReportData,
    isValidQueryState,
    shouldFetchReportData,
    trackQuantifyQueryEvents,
  ]);

  const onChange = useCallback(
    ({ whereClause }: { whereClause: WhereClause | null }) => {
      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: {
          formulaQuery: {
            denominatorWhereClause: whereClause ?? undefined,
          },
          shouldShowTopDataPoints: true,
          visibleDataPoints: [],
        },
      });

      setShouldFetchReportData(true);
      try {
        trackQuantifyQueryEvents('Quantify - Denominator WhereClause Changed', {
          denominatorWhereClauseEQL: mapToEQL({
            whereClause: whereClause ?? EmptyWhereClause,
            expressionType: ExpressionType.Records,
            sourceDetailsMap: sourceDetailsMap ?? new Map(),
          }),
        });
      } catch {}
    },
    [dispatch, sourceDetailsMap, trackQuantifyQueryEvents],
  );

  const renderWhereClauseField = useCallback(() => {
    if (!dashboardWhereClause || !shouldIncludeDashboardFilters) {
      return (
        <WhereClauseField
          whereClause={primaryDenominatorWhereClause}
          onChange={onChange}
          modalTitle="Add Denominator Filters"
          label={null}
          shouldOpenModal={shouldOpenQBModal}
          onShouldOpenModelChange={setShouldOpenQBModal}
          disableOpenModal={isReadOnly}
          whereClausePreviewProps={whereClausePreviewProps}
        />
      );
    }

    if (!shouldShowDashboardFiltersSeparately) {
      return (
        <WhereClauseField
          whereClause={denominatorWhereClause}
          onChange={onChange}
          modalTitle="Add Denominator Filters"
          label={null}
          shouldOpenModal={shouldOpenQBModal}
          onShouldOpenModelChange={setShouldOpenQBModal}
          disableOpenModal={isReadOnly}
          whereClausePreviewProps={whereClausePreviewProps}
        />
      );
    }

    if (
      isReadOnly &&
      isEmptyWhereClause(primaryDenominatorWhereClause ?? EmptyWhereClause)
    ) {
      return (
        <DashboardAndAnalysisFiltersPreview
          dashboardWhereClause={dashboardWhereClause}
          analysisWhereClause={
            primaryDenominatorWhereClause ?? EmptyWhereClause
          }
          analysisUseCase={AnalysisUseCase.Default}
          shouldHideHeader
        />
      );
    }

    return (
      <Stack>
        <CombinedDashboardFilterField
          isReadOnly={isReadOnly}
          onRemove={() => setShouldFetchReportData(true)}
        />
        <AndOperatorLabel />
        <CombinedQuantifyFilterField
          whereClause={primaryDenominatorWhereClause}
          onChange={onChange}
          modalTitle="Add Denominator Filters"
          customPreviewTitle="Denominator Filters"
          label={null}
          shouldOpenModal={shouldOpenQBModal}
          onShouldOpenModelChange={setShouldOpenQBModal}
          disableOpenModal={isReadOnly}
          whereClausePreviewProps={whereClausePreviewProps}
          onClear={() => {
            onChange({ whereClause: null });
          }}
        />
      </Stack>
    );
  }, [
    dashboardWhereClause,
    shouldIncludeDashboardFilters,
    shouldShowDashboardFiltersSeparately,
    isReadOnly,
    primaryDenominatorWhereClause,
    onChange,
    shouldOpenQBModal,
    setShouldOpenQBModal,
    whereClausePreviewProps,
    denominatorWhereClause,
  ]);

  const shouldShowZeroState =
    isEmptyWhereClause(primaryDenominatorWhereClause ?? EmptyWhereClause) &&
    isEmptyWhereClause(dashboardWhereClause ?? EmptyWhereClause);

  return (
    <>
      <Typography
        variant="body2"
        color="inherit"
        sx={{
          display: shouldShowZeroState ? 'block' : 'none',
        }}
      >
        No filters applied
      </Typography>
      <Box
        sx={{
          display: shouldShowZeroState ? 'none' : 'block',
        }}
      >
        <QueryBuilderStoreProvider>
          {renderWhereClauseField()}
        </QueryBuilderStoreProvider>
      </Box>
    </>
  );
}
